'use client'

// Drives the "connect an ad account" step of the on-page Free Ads Account
// Audit. Because the audit runs in a REAL brand's chat on a REAL org-scoped
// credential, connecting means:
//   1. POST /api/free-ads-audit/workspace — silently create a real self-auth
//      org + incomplete brand (once; the coordinates are cached so a retry
//      never creates a second org).
//   2. Activate that org client-side (tokenManager.setActiveOrg + force-refresh)
//      so the org-scoped OAuth authorize endpoint accepts the request — the JWT
//      otherwise lags the just-created org.
//   3. Start the standard native OAuth redirect for meta/google with the audit
//      brand slug, returning to /free-ads-audit. The shared OAuth callback page
//      runs account discovery → selection → import, and auto-binds the imported
//      accounts to the single brand (ResourceBrandGrant), then bounces back here
//      via the redirect allowlist (safeOAuthFinalRedirectPath).
//
// On return, the page reads the persisted run intent and, once the visitor
// submits, opens the audit in the brand's chat (`useFreeAdsAuditStart`).

import { useCallback, useRef, useState } from 'react'

import { useAuthContext } from '@/hooks/use-auth-context'
import { getApiBaseUrl } from '@/lib/api-config'
import { getBackendAuthToken } from '@/lib/backend-auth-token'
import { FREE_ADS_AUDIT_PATH } from '@/app/free-ads-audit/_components/free-ads-audit-url'

export type FreeAdsAuditPlatform = 'google' | 'meta'

// Matches the key the shared OAuth callback page reads.
const OAUTH_STATE_KEY = 'integration_oauth_state'
// Persists which brand/platform to audit once the OAuth round-trip returns.
const AUDIT_RUN_INTENT_KEY = 'free_ads_audit_run_intent'
// Caches the created workspace so a connect retry reuses the same org+brand.
// Scoped to the signed-in user: sessionStorage survives a sign-out → different
// account sign-in on the same origin, so an unscoped cache would let the second
// user reuse the first user's org/brand.
const AUDIT_WORKSPACE_KEY = 'free_ads_audit_workspace'

interface AuditWorkspace {
  orgId: string
  authOrgId: string
  orgSlug: string
  brandId: string
  brandSlug: string
}

// What actually lands in sessionStorage: the workspace plus the user it belongs
// to, so a reader can reject coordinates minted for a different account.
interface StoredAuditWorkspace {
  workspace: AuditWorkspace
  userId: string | null
}

function readStoredAuditWorkspace(): StoredAuditWorkspace | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(AUDIT_WORKSPACE_KEY)
    if (!raw) return null
    const stored = JSON.parse(raw) as StoredAuditWorkspace
    // Ignore pre-scoping (untagged) blobs and anything malformed.
    return stored?.workspace?.brandId ? stored : null
  } catch {
    return null
  }
}

export interface FreeAdsAuditRunIntent {
  brandId: string
  platform: FreeAdsAuditPlatform
  websiteUrl?: string
}

// What actually lands in sessionStorage: the run intent plus the user it belongs
// to. Scoped like the workspace cache above — sessionStorage survives a sign-out
// -> different sign-in on the same tab, so an unscoped intent would show the
// second user the first user's "<platform> connected" ready screen.
interface StoredAuditRunIntent {
  intent: FreeAdsAuditRunIntent
  userId: string | null
}

function readStoredAuditRunIntent(): StoredAuditRunIntent | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(AUDIT_RUN_INTENT_KEY)
    if (!raw) return null
    const stored = JSON.parse(raw) as StoredAuditRunIntent
    // Ignore pre-scoping (untagged, flat) blobs and anything malformed.
    return stored?.intent?.brandId ? stored : null
  } catch {
    return null
  }
}

// One-shot consume of the run intent, scoped to the signed-in user: hand back
// the coordinates and clear the slot so it can't run twice. (The on-page resume
// uses peek, not take, so a refresh keeps the ready screen; the audit start
// consumes it once the audit is handed to the chat.) Returns null when the
// intent belongs to a different account (sessionStorage survives a sign-out ->
// different sign-in on the same tab) or is a pre-scoping blob.
export function takeFreeAdsAuditRunIntent(
  expectedUserId: string | null,
): FreeAdsAuditRunIntent | null {
  const stored = readStoredAuditRunIntent()
  if (!stored) return null
  // Consume the slot regardless of owner so a stale cross-user intent can't
  // linger; only hand back coordinates that belong to the current user.
  sessionStorage.removeItem(AUDIT_RUN_INTENT_KEY)
  if (!expectedUserId || stored.userId !== expectedUserId) return null
  return stored.intent
}

// Read the run intent WITHOUT consuming it, scoped to the signed-in user. The
// post-connect "enter your website" screen reads this to learn which platform
// was connected; leaving it in place means a refresh on that screen keeps the
// ready-to-run state. Returning null for a different account stops that screen
// from showing the first user's connection to the second. The run itself stays
// single via the backend per-user quota.
export function peekFreeAdsAuditRunIntent(
  expectedUserId: string | null,
): FreeAdsAuditRunIntent | null {
  const stored = readStoredAuditRunIntent()
  if (!stored || !expectedUserId || stored.userId !== expectedUserId) return null
  return stored.intent
}

export function buildFreeAdsAuditAuthorizeUrl(
  baseUrl: string,
  platform: FreeAdsAuditPlatform,
  brandSlug: string,
): URL {
  const authorizeUrl = new URL(
    `/api/credentials/oauth/${platform}/authorize`,
    baseUrl,
  )
  authorizeUrl.searchParams.set('brand_slug', brandSlug)
  authorizeUrl.searchParams.set('redirect_to', FREE_ADS_AUDIT_PATH)
  return authorizeUrl
}

export function useFreeAdsAuditConnect() {
  const { getToken, user } = useAuthContext()
  // Track WHICH platform is connecting (not just a boolean) so only the clicked
  // button shows a spinner; the other controls disable without spinning.
  const [connectingPlatform, setConnectingPlatform] =
    useState<FreeAdsAuditPlatform | null>(null)
  const [error, setError] = useState<string | null>(null)
  const inFlightRef = useRef(false)
  const isConnecting = connectingPlatform !== null

  const ensureWorkspace = useCallback(
    async (baseUrl: string): Promise<AuditWorkspace> => {
      const userId = user?.id ?? null
      // Reuse the cache only when it belongs to the CURRENT user. On a miss (or a
      // different account on this origin) re-POST: the endpoint is idempotent per
      // user (server-side advisory-locked reuse), so this never mints a second
      // org. It just returns this user's own workspace.
      const cached = readStoredAuditWorkspace()
      if (cached && userId && cached.userId === userId) return cached.workspace

      const token = await getBackendAuthToken(getToken, { skipCache: true })
      // Free Ads Audit bootstraps an org before the active-org token is switched.
      // web-arch-allow-direct-fetch: this one-off request owns its token.
      const res = await fetch(`${baseUrl}/api/free-ads-audit/workspace`, {
        method: 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name: 'My workspace' }),
      })
      if (!res.ok) throw new Error(`workspace_http_${res.status}`)
      const body = (await res.json()) as { data?: AuditWorkspace }
      if (!body.data?.brandId) throw new Error('workspace_invalid_response')
      const stored: StoredAuditWorkspace = { workspace: body.data, userId }
      sessionStorage.setItem(AUDIT_WORKSPACE_KEY, JSON.stringify(stored))
      return body.data
    },
    [getToken, user],
  )

  const connect = useCallback(
    async (platform: FreeAdsAuditPlatform, websiteUrl?: string) => {
      if (typeof window === 'undefined' || inFlightRef.current) return
      inFlightRef.current = true
      setError(null)
      setConnectingPlatform(platform)
      try {
        const baseUrl = getApiBaseUrl()
        const workspace = await ensureWorkspace(baseUrl)

        // Activate the just-created org so the org-scoped authorize endpoint
        // accepts the request (the token otherwise lags the new org).
        const { tokenManager } = await import('@/lib/self-auth/token-manager')
        tokenManager.setActiveOrg(workspace.authOrgId)
        await tokenManager.getToken({ forceRefresh: true })

        // Persist what to audit once OAuth returns to /free-ads-audit, tagged
        // with the user it belongs to so a later account on this tab can't read
        // it (see StoredAuditRunIntent).
        const intent: FreeAdsAuditRunIntent = {
          brandId: workspace.brandId,
          platform,
          ...(websiteUrl ? { websiteUrl } : {}),
        }
        const storedIntent: StoredAuditRunIntent = {
          intent,
          userId: user?.id ?? null,
        }
        sessionStorage.setItem(AUDIT_RUN_INTENT_KEY, JSON.stringify(storedIntent))

        const token = await getBackendAuthToken(getToken, { skipCache: true })
        const authorizeUrl = buildFreeAdsAuditAuthorizeUrl(
          baseUrl,
          platform,
          workspace.brandSlug,
        )
        // This follows the active-org token switch above.
        // web-arch-allow-direct-fetch: use a freshly forced token for the new org.
        const res = await fetch(authorizeUrl.toString(), {
          method: 'GET',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        })
        if (!res.ok) throw new Error(`authorize_http_${res.status}`)
        const body = (await res.json()) as {
          data?: { authorize_url?: string; state?: string }
        }
        const authorize = body.data
        if (!authorize?.authorize_url || !authorize.state) {
          throw new Error('authorize_invalid_response')
        }

        // Hand the shared OAuth callback page the state it needs to finish the
        // round-trip and bounce back to /free-ads-audit.
        sessionStorage.setItem(
          OAUTH_STATE_KEY,
          JSON.stringify({
            state: authorize.state,
            platform,
            redirectTo: FREE_ADS_AUDIT_PATH,
          }),
        )
        window.location.assign(authorize.authorize_url)
      } catch (err) {
        // Don't strand the run intent on a failed start.
        try {
          sessionStorage.removeItem(AUDIT_RUN_INTENT_KEY)
        } catch {
          /* ignore */
        }
        setError((err as Error).message)
        setConnectingPlatform(null)
        inFlightRef.current = false
      }
    },
    [ensureWorkspace, getToken, user],
  )

  return { connect, isConnecting, connectingPlatform, error }
}
